import React, { useRef, useEffect } from "react";

// ── Constants ────────────────────────────────────────────────────────────────
const FOV    = 420;
const FAR    = 2600;
const SHIP_R = 130;   // larger movement circle
const BSPD   = 210;

const rand  = (a, b) => a + Math.random() * (b - a);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Project world (wx,wy,wz) → [screenX, screenY, scale]
const pr = (wx, wy, wz, cx, cy) => {
    const s = FOV / Math.max(wz, 1);
    return [cx + wx * s, cy + wy * s, s];
};

const SpaceDodger = () => {
    const cvs = useRef(null);
    const gs  = useRef(null);
    
    useEffect(() => {
        const canvas = cvs.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        // ── Sizing ─────────────────────────────────────────────────────────
        const resize = () => {
            canvas.width  = Math.min(canvas.parentElement?.clientWidth ?? 680, 680);
            canvas.height = 360;
        };
        resize();
        window.addEventListener("resize", resize);

        // ── Factories ──────────────────────────────────────────────────────
        const mkStar = () => ({
            wx: rand(-1600, 1600), wy: rand(-800, 800), wz: rand(120, FAR),
            spd: rand(1.5, 5), bright: Math.random() < 0.1, bloom: rand(2.5, 7),
        });

        // Planets spawn far apart — only 3 at a time, widely spaced z values
        const mkPlanet = (zHint) => ({
            wx: rand(-1200, 1200), wy: rand(-600, 600),
            wz: zHint ?? rand(FAR * 0.55, FAR * 0.95),
            wr: rand(65, 200),
            hue: Math.floor(rand(0, 360)),
            sat: Math.floor(rand(38, 65)),
            rings: Math.random() < 0.38,
            spd: rand(0.04, 0.18),
        });

        // KEY FIX: asteroids aimed using a "danger depth" D so they project
        // onto the ship's screen position when wz ≈ D — not at FAR_Z.
        const mkAsteroid = (sx, sy, cx, cy, spdMul) => {
            const aimed = Math.random() < 0.55;
            const D = rand(150, 300); // depth where aimed asteroid aligns with ship
            const wx = aimed
                ? (sx - cx) / FOV * D + rand(-30, 30)
                : rand(-130, 130);
            const wy = aimed
                ? (sy - cy) / FOV * D + rand(-20, 20)
                : rand(-90, 90);
            const sides = 7 + Math.floor(rand(0, 4));
            const verts = Array.from({ length: sides }, (_, i) => {
                const a = (i / sides) * Math.PI * 2;
                return [Math.cos(a) * rand(0.65, 0.98), Math.sin(a) * rand(0.65, 0.98)];
            });
            return {
                wx, wy, wz: FAR,
                wr: rand(18, 50),
                spd: rand(0.36, 0.78) * spdMul,
                verts,
                rot: rand(0, Math.PI * 2),
                rotSpd: rand(-0.02, 0.02),
                hue: rand(16, 56),
            };
        };

        const mkState = () => {
            const W = canvas.width, H = canvas.height;
            // Three planets at well-spread z depths so they don't clump
            const planets = [
                mkPlanet(FAR * 0.92),
                mkPlanet(FAR * 0.70),
                mkPlanet(FAR * 0.55),
            ];
            return {
                phase: "idle",
                W, H, cx: W / 2, cy: H / 2,
                score: 0, hiScore: gs.current?.hiScore ?? 0, tick: 0,
                ship: { sx: W / 2, sy: H / 2, vx: 0, vy: 0 },
                bullets: [], asteroids: [], particles: [],
                stars: Array.from({ length: 180 }, mkStar),
                planets,
                astTimer: 0, astInterval: 130,
                lastShot: -999,
                keys: {}, mouse: { x: W / 2, y: H / 2, held: false },
            };
        };

        gs.current = mkState();

        // ── Input ──────────────────────────────────────────────────────────
        const startGame = () => {
            const hi = gs.current?.hiScore ?? 0;
            gs.current = mkState();
            gs.current.phase = "playing";
            gs.current.hiScore = hi;
        };

        const onKD = (e) => {
            const s = gs.current; if (!s) return;
            s.keys[e.key] = true;
            if ((e.key === " " || e.key === "Enter") && s.phase !== "playing") startGame();
            if (e.key === " ") e.preventDefault();
            if (["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"].includes(e.key) && s.phase === "playing") e.preventDefault();
        };
        const onKU = (e) => { if (gs.current) gs.current.keys[e.key] = false; };

        const cpx = (e) => { const r = canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
        const onMM = (e) => { if (!gs.current) return; const [x,y] = cpx(e); gs.current.mouse.x = x; gs.current.mouse.y = y; };
        const onMD = (e) => {
            const s = gs.current; if (!s) return;
            const [x,y] = cpx(e); s.mouse.x = x; s.mouse.y = y; s.mouse.held = true;
            if (s.phase !== "playing") startGame();
        };
        const onMU = () => { if (gs.current) gs.current.mouse.held = false; };
        const onTS = (e) => {
            const s = gs.current; if (!s) return;
            const r = canvas.getBoundingClientRect(); const t = e.touches[0];
            s.mouse.x = t.clientX - r.left; s.mouse.y = t.clientY - r.top; s.mouse.held = true;
            if (s.phase !== "playing") startGame();
        };
        const onTM = (e) => {
            const s = gs.current; if (!s) return;
            const r = canvas.getBoundingClientRect(); const t = e.touches[0];
            s.mouse.x = t.clientX - r.left; s.mouse.y = t.clientY - r.top;
        };
        const onTE = () => { if (gs.current) gs.current.mouse.held = false; };

        window.addEventListener("keydown", onKD);
        window.addEventListener("keyup",   onKU);
        canvas.addEventListener("mousemove",  onMM);
        canvas.addEventListener("mousedown",  onMD);
        canvas.addEventListener("mouseup",    onMU);
        canvas.addEventListener("mouseleave", onMU);
        canvas.addEventListener("touchstart", onTS, { passive: true });
        canvas.addEventListener("touchmove",  onTM, { passive: true });
        canvas.addEventListener("touchend",   onTE);

        // ── Ship image ─────────────────────────────────────────────────────
        const shipImg = new Image();
        shipImg.src = `${process.env.PUBLIC_URL}/venturestar.png.png`;

        // ── Draw helpers ───────────────────────────────────────────────────

        const drawStars = (s) => {
            for (const st of s.stars) {
                const [sx, sy, sc] = pr(st.wx, st.wy, st.wz, s.cx, s.cy);
                if (sc > 3 || sx < -30 || sx > s.W + 30 || sy < -30 || sy > s.H + 30) continue;
                if (st.bright) {
                    const gr = Math.min(st.bloom * sc * 50 + 1.5, 80);
                    const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, gr);
                    g.addColorStop(0, "rgba(255,248,205,0.9)");
                    g.addColorStop(0.14, "rgba(200,232,255,0.26)");
                    g.addColorStop(1, "rgba(140,185,255,0)");
                    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(sx, sy, gr, 0, Math.PI * 2); ctx.fill();
                    ctx.save();
                    ctx.strokeStyle = "rgba(255,252,215,0.2)"; ctx.lineWidth = 0.5;
                    const sl = gr * 0.58;
                    ctx.beginPath(); ctx.moveTo(sx - sl, sy); ctx.lineTo(sx + sl, sy); ctx.stroke();
                    ctx.beginPath(); ctx.moveTo(sx, sy - sl); ctx.lineTo(sx, sy + sl); ctx.stroke();
                    ctx.restore();
                    ctx.fillStyle = "rgba(255,255,248,0.95)";
                    ctx.beginPath(); ctx.arc(sx, sy, Math.min(sc * 8, 4), 0, Math.PI * 2); ctx.fill();
                } else {
                    ctx.fillStyle = `rgba(210,228,255,${clamp(sc * 50, 0.05, 0.75)})`;
                    ctx.beginPath(); ctx.arc(sx, sy, Math.min(sc * 4, 2.2), 0, Math.PI * 2); ctx.fill();
                }
            }
        };

        const drawPlanets = (s) => {
            for (const p of s.planets) {
                const [sx, sy, sc] = pr(p.wx, p.wy, p.wz, s.cx, s.cy);
                const r = p.wr * sc;
                if (r < 1.5 || sx + r < -10 || sx - r > s.W + 10 || sy + r < -10 || sy - r > s.H + 10) continue;
                ctx.save();
                const lx = sx - r * 0.32, ly = sy - r * 0.38;
                const g = ctx.createRadialGradient(lx, ly, r * 0.06, sx, sy, r);
                const { hue: h, sat } = p;
                g.addColorStop(0,    `hsl(${h},${sat}%,74%)`);
                g.addColorStop(0.35, `hsl(${h},${sat * 0.84}%,40%)`);
                g.addColorStop(0.76, `hsl(${h+10},${sat * 0.66}%,18%)`);
                g.addColorStop(1,    `hsl(${h},${sat * 0.46}%,5%)`);
                ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2);
                ctx.fillStyle = g; ctx.fill();
                const ag = ctx.createRadialGradient(sx, sy, r * 0.88, sx, sy, r * 1.22);
                ag.addColorStop(0,    `hsla(${h+15},65%,56%,0)`);
                ag.addColorStop(0.55, `hsla(${h+15},65%,56%,0.15)`);
                ag.addColorStop(1,    `hsla(${h+15},65%,56%,0)`);
                ctx.fillStyle = ag; ctx.beginPath(); ctx.arc(sx, sy, r * 1.22, 0, Math.PI * 2); ctx.fill();
                if (p.rings && r > 6) {
                    ctx.translate(sx, sy); ctx.scale(1, 0.26);
                    ctx.strokeStyle = `hsla(${h},32%,58%,0.34)`; ctx.lineWidth = r * 0.2;
                    ctx.beginPath(); ctx.arc(0, 0, r * 1.72, 0, Math.PI * 2); ctx.stroke();
                }
                ctx.restore();
            }
        };

        const drawAsteroids = (s) => {
            const sorted = [...s.asteroids].sort((a, b) => b.wz - a.wz);
            for (const a of sorted) {
                const [sx, sy, sc] = pr(a.wx, a.wy, a.wz, s.cx, s.cy);
                const r = a.wr * sc;
                if (r < 0.5) continue;
                ctx.save(); ctx.translate(sx, sy); ctx.rotate(a.rot);
                ctx.beginPath();
                ctx.moveTo(a.verts[0][0] * r, a.verts[0][1] * r);
                for (let i = 1; i < a.verts.length; i++) ctx.lineTo(a.verts[i][0] * r, a.verts[i][1] * r);
                ctx.closePath();
                const lg = ctx.createRadialGradient(-r*0.3, -r*0.3, r*0.03, 0, 0, r*1.28);
                lg.addColorStop(0, `hsl(${a.hue},24%,68%)`);
                lg.addColorStop(0.5, `hsl(${a.hue},18%,35%)`);
                lg.addColorStop(1, `hsl(${a.hue},14%,8%)`);
                ctx.fillStyle = lg; ctx.fill();
                ctx.strokeStyle = `hsla(${a.hue},16%,50%,0.25)`; ctx.lineWidth = 0.6; ctx.stroke();
                ctx.restore();
            }
        };

        const drawBullets = (s) => {
            for (const b of s.bullets) {
                const sc = clamp(FOV / b.wz, 0, 5);
                const r = Math.max(1.2, sc * 4);
                const g = ctx.createRadialGradient(b.sx, b.sy, 0, b.sx, b.sy, r * 5);
                g.addColorStop(0, "rgba(140,255,195,0.95)");
                g.addColorStop(0.22, "rgba(55,220,140,0.42)");
                g.addColorStop(1, "rgba(0,175,100,0)");
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(b.sx, b.sy, r * 5, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = "#e0fff2"; ctx.beginPath(); ctx.arc(b.sx, b.sy, r, 0, Math.PI * 2); ctx.fill();
            }
        };

        const drawParticles = (s) => {
            for (const p of s.particles) {
                const t = p.life / p.maxLife;
                const [sx, sy] = pr(p.wx, p.wy, p.wz, s.cx, s.cy);
                const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, p.r * t * 3.5);
                g.addColorStop(0, `hsla(${p.hue},85%,68%,${t * 0.9})`);
                g.addColorStop(1, `hsla(${p.hue+18},60%,36%,0)`);
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(sx, sy, p.r * t * 3.5 + 0.3, 0, Math.PI * 2); ctx.fill();
            }
        };

        const drawShip = (s) => {
            const { sx, sy, vx } = s.ship;
            const roll = clamp(vx * 0.042, -0.4, 0.4);
            const imgW = 100, imgH = 90;
            ctx.save();
            ctx.translate(sx, sy);
            ctx.rotate(roll);
            if (shipImg.complete && shipImg.naturalWidth > 0) {
                ctx.drawImage(shipImg, -imgW / 4, -imgH / 4, imgW, imgH);
            }
            ctx.restore();
        };

        // ── Game loop ──────────────────────────────────────────────────────
        let raf;
        const loop = () => {
            raf = requestAnimationFrame(loop);
            const s = gs.current; if (!s) return;
            s.W = canvas.width; s.H = canvas.height;
            s.cx = s.W / 2; s.cy = s.H / 2;

            ctx.fillStyle = "#01030c"; ctx.fillRect(0, 0, s.W, s.H);

            if (s.phase === "playing") {
                s.tick++;
                const spd = 1 + s.tick / 2200;

                // Stars
                for (const st of s.stars) {
                    st.wz -= st.spd * 3.5 * spd;
                    if (st.wz < 15) { Object.assign(st, mkStar()); st.wz = FAR; }
                }
                // Planets — advance slowly
                for (const p of s.planets) {
                    p.wz -= p.spd * 1.5 * spd;
                    if (p.wz < 80) { Object.assign(p, mkPlanet()); p.wz = FAR * rand(0.7, 0.98); }
                }

                // Ship
                let tx = s.mouse.x, ty = s.mouse.y;
                if (s.keys["a"] || s.keys["ArrowLeft"])  tx -= 5;
                if (s.keys["d"] || s.keys["ArrowRight"]) tx += 5;
                if (s.keys["w"] || s.keys["ArrowUp"])    ty -= 5;
                if (s.keys["s"] || s.keys["ArrowDown"])  ty += 5;
                const dx = tx - s.cx, dy = ty - s.cy;
                const dd = Math.hypot(dx, dy);
                if (dd > SHIP_R) { tx = s.cx + dx/dd*SHIP_R; ty = s.cy + dy/dd*SHIP_R; }
                s.ship.vx = tx - s.ship.sx; s.ship.vy = ty - s.ship.sy;
                s.ship.sx += s.ship.vx * 0.12; s.ship.sy += s.ship.vy * 0.12;

                // Shoot
                if (s.mouse.held || s.keys[" "]) {
                    const cd = Math.max(5, 14 - Math.floor(s.tick / 650));
                    if (s.tick - s.lastShot >= cd) {
                        s.lastShot = s.tick;
                        s.bullets.push({ sx: s.ship.sx, sy: s.ship.sy, wz: 55 });
                    }
                }
                for (const b of s.bullets) b.wz += BSPD * spd;
                s.bullets = s.bullets.filter(b => b.wz < FAR + 200);

                // Spawn asteroids (start slower, ramp up)
                s.astTimer++;
                s.astInterval = Math.max(14, 130 - s.tick / 68);
                if (s.astTimer >= s.astInterval) {
                    s.astTimer = 0;
                    const n = s.tick > 2200 ? 2 : 1;
                    for (let i = 0; i < n; i++)
                        s.asteroids.push(mkAsteroid(s.ship.sx, s.ship.sy, s.cx, s.cy, spd));
                }
                for (const a of s.asteroids) { a.wz -= a.spd * 4.5 * spd; a.rot += a.rotSpd; }
                s.asteroids = s.asteroids.filter(a => a.wz > 8);

                // Bullet ↔ asteroid: screen-space check when z depths match
                const dA = new Set(), dB = new Set();
                for (let bi = 0; bi < s.bullets.length; bi++) {
                    if (dB.has(bi)) continue;
                    const b = s.bullets[bi];
                    for (let ai = 0; ai < s.asteroids.length; ai++) {
                        if (dA.has(ai)) continue;
                        const a = s.asteroids[ai];
                        if (Math.abs(b.wz - a.wz) > 220) continue;
                        const [ax, ay, asc] = pr(a.wx, a.wy, a.wz, s.cx, s.cy);
                        if (Math.hypot(ax - b.sx, ay - b.sy) < a.wr * asc + 9) {
                            dA.add(ai); dB.add(bi); s.score += 10;
                            for (let k = 0; k < 10; k++)
                                s.particles.push({ wx: a.wx+rand(-a.wr,a.wr), wy: a.wy+rand(-a.wr,a.wr), wz: a.wz, r: rand(2,7), hue: rand(15,55), life:35, maxLife:35 });
                        }
                    }
                }
                s.asteroids = s.asteroids.filter((_, i) => !dA.has(i));
                s.bullets   = s.bullets.filter(  (_, i) => !dB.has(i));

                // Particles
                for (const p of s.particles) p.life--;
                s.particles = s.particles.filter(p => p.life > 0);

                // Ship ↔ asteroid collision
                // Check all asteroids that are within the danger zone depth.
                // Since aimed asteroids align with the ship at depth D (150-300),
                // check from wz=0 up to wz=380.
                for (const a of s.asteroids) {
                    if (a.wz > 380) continue;
                    const [ax, ay, asc] = pr(a.wx, a.wy, a.wz, s.cx, s.cy);
                    const hitR = a.wr * asc * 0.65 + 10;
                    if (Math.hypot(ax - s.ship.sx, ay - s.ship.sy) < hitR) {
                        s.phase = "dead";
                        if (s.score > s.hiScore) s.hiScore = s.score;
                        for (let k = 0; k < 22; k++)
                            s.particles.push({ wx: a.wx, wy: a.wy, wz: a.wz, r: rand(3,10), hue: rand(15,60), life:48, maxLife:48 });
                        break;
                    }
                }

                if (s.tick % 6 === 0) s.score++;

                // Render
                drawPlanets(s); drawStars(s); drawParticles(s);
                drawAsteroids(s); drawBullets(s); drawShip(s);

                // Vignette
                const vg = ctx.createRadialGradient(s.cx, s.cy, s.H*0.12, s.cx, s.cy, s.H*0.85);
                vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,15,0.55)");
                ctx.fillStyle = vg; ctx.fillRect(0, 0, s.W, s.H);

                // Faint boundary ring
                ctx.save();
                ctx.setLineDash([3, 8]);
                ctx.strokeStyle = "rgba(80,150,255,0.07)"; ctx.lineWidth = 1;
                ctx.beginPath(); ctx.arc(s.cx, s.cy, SHIP_R, 0, Math.PI*2); ctx.stroke();
                ctx.restore();

                // HUD
                ctx.fillStyle = "rgba(168,228,255,0.7)"; ctx.font = "bold 12px monospace";
                ctx.textAlign = "left";  ctx.fillText(`SCORE  ${String(s.score).padStart(5,"0")}`, 10, 18);
                ctx.textAlign = "right"; ctx.fillText(`BEST   ${String(s.hiScore).padStart(5,"0")}`, s.W-10, 18);

                if (s.tick < 200) {
                    ctx.fillStyle = `rgba(148,215,255,${(200-s.tick)/200*0.48})`;
                    ctx.font = "10px monospace"; ctx.textAlign = "center";
                    ctx.fillText("MOUSE to steer  ·  HOLD to shoot  ·  WASD also works", s.cx, s.H-8);
                }

            } else {
                for (const st of s.stars) { st.wz -= st.spd*1.0; if (st.wz < 15) { Object.assign(st, mkStar()); st.wz = FAR; } }
                for (const p of s.planets) { p.wz -= p.spd*0.6; if (p.wz < 80) { Object.assign(p, mkPlanet()); p.wz = FAR*rand(0.7,0.98); } }
                drawPlanets(s); drawStars(s);
                if (s.particles.length) {
                    for (const p of s.particles) p.life--;
                    s.particles = s.particles.filter(p => p.life > 0);
                    drawParticles(s);
                }
                drawShip(s);

                ctx.fillStyle = "rgba(1,3,12,0.5)"; ctx.fillRect(0, 0, s.W, s.H);
                ctx.textAlign = "center";
                if (s.phase === "dead") {
                    ctx.fillStyle = "#ff7060"; ctx.font = "bold 20px monospace";
                    ctx.fillText("DESTROYED", s.cx, s.cy - 14);
                    ctx.fillStyle = "rgba(172,228,255,0.86)"; ctx.font = "13px monospace";
                    ctx.fillText(`Score: ${s.score}   Best: ${s.hiScore}`, s.cx, s.cy + 6);
                    ctx.fillStyle = "rgba(132,202,255,0.5)"; ctx.font = "11px monospace";
                    ctx.fillText("click · touch · space to retry", s.cx, s.cy + 26);
                } else {
                    ctx.fillStyle = "rgba(172,228,255,0.88)"; ctx.font = "bold 15px monospace";
                    ctx.fillText("NO CAMPAIGNS YET", s.cx, s.cy - 18);
                    ctx.fillStyle = "rgba(138,206,255,0.56)"; ctx.font = "11px monospace";
                    ctx.fillText("dodge & destroy asteroids while you wait", s.cx, s.cy + 2);
                    ctx.fillStyle = "rgba(100,182,255,0.4)"; ctx.font = "10px monospace";
                    ctx.fillText("mouse to steer  ·  hold to shoot  ·  space to start", s.cx, s.cy + 20);
                }
            }
        };
        loop();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize",   resize);
            window.removeEventListener("keydown",  onKD);
            window.removeEventListener("keyup",    onKU);
            canvas.removeEventListener("mousemove",  onMM);
            canvas.removeEventListener("mousedown",  onMD);
            canvas.removeEventListener("mouseup",    onMU);
            canvas.removeEventListener("mouseleave", onMU);
            canvas.removeEventListener("touchstart", onTS);
            canvas.removeEventListener("touchmove",  onTM);
            canvas.removeEventListener("touchend",   onTE);
        };
    }, []);

    return (
        <canvas
            ref={cvs}
            style={{
                display: "block", margin: "0 auto",
                borderRadius: "10px", cursor: "none",
                border: "1px solid rgba(80,160,255,0.1)",
                maxWidth: "100%",
            }}
        />
    );
};

export default SpaceDodger;
